import { csvEscape, toUtcYmdHms } from "../../company/utils/csvAndDateUtils";
import { countryCodeToName } from "../InternalUsageTab/utils";
import type { UserSessionEntry } from "./types";
import { downloadCsvFile, formatDurationMinutes, resolveUserDisplayName } from "./utils";

interface ExportColumn {
  header: string;
  accessor: (entry: UserSessionEntry) => string;
}

/**
 * Export session history data to CSV and trigger a download.
 *
 * @param data - The session entries to export
 * @param columns - Column definitions matching what's displayed in the table
 * @param filename - The name for the downloaded file
 */
export function exportSessionHistoryCsv(
  data: UserSessionEntry[],
  columnHeaders: {
    date: string;
    userId: string;
    experience: string;
    duration: string;
    country: string;
    multiplayerSessionId: string;
  },
  filename: string,
  userDisplayNames?: Map<string, string>,
): void {
  const columns: ExportColumn[] = [
    { header: columnHeaders.date, accessor: (e) => toUtcYmdHms(e.date) },
    {
      header: columnHeaders.userId,
      accessor: (e) => resolveUserDisplayName(e.userId, userDisplayNames),
    },
    { header: columnHeaders.experience, accessor: (e) => e.experienceName },
    {
      header: columnHeaders.duration,
      accessor: (e) => formatDurationMinutes(e.durationSeconds),
    },
    {
      header: columnHeaders.country,
      accessor: (e) => (e.country ? countryCodeToName(e.country) : ""),
    },
    {
      header: columnHeaders.multiplayerSessionId,
      accessor: (e) => e.multiplayerSessionId ?? "",
    },
  ];

  const headerRow = columns.map((col) => csvEscape(col.header)).join(",");
  const rows = data.map((entry) => columns.map((col) => csvEscape(col.accessor(entry))).join(","));

  const csvContent = [headerRow, ...rows].join("\r\n");
  downloadCsvFile(csvContent, filename);
}

/** Generate a default filename for the export */
export function makeSessionExportFilename(startDate: Date, endDate: Date): string {
  const fmt = (d: Date) => d.toISOString().slice(0, 10);

  return `user-sessions_${fmt(startDate)}_${fmt(endDate)}.csv`;
}
